import { useState, useEffect, useRef, useMemo } from 'react';

export default function surveyTriggers({
  breakpoint = 810,
  threshold = 1000,
  idleMs = 10000,
  disabled = false,
  // mode: 'below' => width < breakpoint, 'above' => width >= breakpoint, 'both' => ignore width
  mode = 'below',
  requireScroll = false, // default: no scroll-depth requirement
}) {
  const [show, setShow] = useState(false);
  const [closed, setClosed] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // (1) one-shot timer from load
  const [timeReady, setTimeReady] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setTimeReady(true);
    }, idleMs);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [idleMs]);

  // (2) optional scroll gate
  const scrollGateEnabled = requireScroll && Number.isFinite(threshold);
  const [scrolledReady, setScrolledReady] = useState(
    !scrollGateEnabled || window.scrollY > threshold
  );

  useEffect(() => {
    if (!scrollGateEnabled) return;
    const onScroll = () => setScrolledReady(window.scrollY > threshold);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [scrollGateEnabled, threshold]);

  // width tracking (skip listener if mode === 'both')
  useEffect(() => {
    if (mode === 'both') return;
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [mode]);

  // width gate
  const widthOK = useMemo(() => {
    if (mode === 'both') return true; // ignore width entirely
    return mode === 'below' ? windowWidth < breakpoint : windowWidth >= breakpoint;
  }, [mode, windowWidth, breakpoint]);

  // reveal when eligible
  useEffect(() => {
    if (!show && !closed && !disabled && widthOK && timeReady && scrolledReady) {
      setShow(true);
    }
  }, [show, closed, disabled, widthOK, timeReady, scrolledReady]);

  // auto-hide if we lose eligibility (keeps ignoring width if mode === 'both')
  useEffect(() => {
    if (show && (!widthOK || disabled)) setShow(false);
  }, [show, widthOK, disabled]);

  const close = () => { setClosed(true); setShow(false); };

  return {
    visible: show && !closed && widthOK && !disabled,
    show,
    close,
    timeReady,
    scrolledReady,
  };
}
